import { useEffect, useState } from 'react';

export const COOKIE_PREFERENCES_STORAGE_KEY = 'ekc-cookie-preferences';
export const COOKIE_PREFERENCES_EVENT = 'ekc:cookie-preferences-changed';

export type CookiePreferences = {
    version: 1;
    analytics: boolean;
    updatedAt: string;
};

export const isPrivateApplicationPath = (pathname: string) => (
    ['/admin', '/dashboard', '/cms-preview'].some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
);

export const readCookiePreferences = (): CookiePreferences | null => {
    if (typeof window === 'undefined') return null;

    try {
        const rawPreferences = window.localStorage.getItem(COOKIE_PREFERENCES_STORAGE_KEY);
        if (!rawPreferences) return null;

        const parsedPreferences = JSON.parse(rawPreferences);
        if (parsedPreferences?.version !== 1 || typeof parsedPreferences.analytics !== 'boolean') {
            return null;
        }

        return parsedPreferences as CookiePreferences;
    } catch {
        return null;
    }
};

export const saveCookiePreferences = (analytics: boolean): CookiePreferences => {
    const preferences: CookiePreferences = {
        version: 1,
        analytics,
        updatedAt: new Date().toISOString(),
    };

    window.localStorage.setItem(COOKIE_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
    window.dispatchEvent(new CustomEvent<CookiePreferences>(COOKIE_PREFERENCES_EVENT, {
        detail: preferences,
    }));

    return preferences;
};
export const useCookiePreferences = () => {
    const [preferences, setPreferences] = useState<CookiePreferences | null>(() => readCookiePreferences());

    useEffect(() => {
        const syncPreferences = (event: Event) => {
            const nextPreferences = (event as CustomEvent<CookiePreferences>).detail;
            setPreferences(nextPreferences || readCookiePreferences());
        };
        const syncStoredPreferences = (event: StorageEvent) => {
            if (event.key === COOKIE_PREFERENCES_STORAGE_KEY) {
                setPreferences(readCookiePreferences());
            }
        };
        window.addEventListener(COOKIE_PREFERENCES_EVENT, syncPreferences);
        window.addEventListener('storage', syncStoredPreferences);

        return () => {
            window.removeEventListener(COOKIE_PREFERENCES_EVENT, syncPreferences);
            window.removeEventListener('storage', syncStoredPreferences);
        };
    }, []);

    return preferences;
};
